import React, {useEffect, useRef, useState} from "react";
import cx from "classnames";
import {createPortal, flushSync} from "react-dom";
import {Link, useLocation} from "react-router-dom";

import OutlinedButton from "./../../components/buttons/Outlined";
import ContainedButton from "./../../components/buttons/Contained";

import {ArrowDropDown, Close, LogoVedmarket, LogoVedmarketBig, LogoVedmarketMiddle, Menu as MenuIcon} from "@vedmarket/ui-kit";
import styles from './HeroSection.module.scss';
import DesktopMenuText from './DesktopMenuText';
import SubmenuText from '../../components/typography/SubmenuText';
import {typografText} from "../../utils/typograf";
import {EMOJIS} from "../../../../../config/constants";
import IntroSubtitle from "../../components/typography/IntroSubtitle";
import IntroHeader from "../../components/typography/IntroHeader";
import IntroBody from "../../components/typography/IntroBody";
import Menu from "../../components/typography/Menu";
import {getPartnerLanguage, partnerPath, translatePartnerText} from '../../locales/language';
import type {PartnerVariant} from '../../types';
import certCentersRu from '../../locales/certCentersRu';

type HeroLink = {
    href: string;
    text: string;
    isNewTab?: boolean;
    children?: HeroLink[];
};

const navigationLinks: HeroLink[] = [
    {href: '#benefits', text: 'Features'},
    {href: '#cooperation', text: 'How it works'},
    {
        href: '#benefits',
        text: 'Partners',
        children: [
            {href: '/partners/transport', text: 'Transport companies'},
            {href: '/partners/certification', text: 'Certification centers'},
            {href: '/partners/customs-brokers', text: 'Customs brokers'},
            {href: '/partners/banks', text: 'Banks'},
        ],
    },
    {
        href: '#examples',
        text: 'More',
        children: [
            {href: 'https://help.vedmarket.ru/', text: 'FAQ', isNewTab: true},
            {href: 'http://vedmarket.ru/blog', text: 'Blog', isNewTab: true},
        ],
    },
    {href: '#contacts', text: 'Contact'},
];

const LOGIN_LINK = 'https://my.vedmarket.ru/company/login';
const REGISTER_LINK = 'https://my.vedmarket.ru/company/register';
const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1200;
const MOBILE_MENU_ANIMATION_DURATION = 240;

const swingProgress = (progress: number) => 0.5 - Math.cos(progress * Math.PI) / 2;

type HeroSectionProps = {
    backgroundContainer?: HTMLDivElement | null;
    variant?: PartnerVariant;
};

function HeroSection({backgroundContainer, variant = 'transport'}: HeroSectionProps) {
    const isEnglish = getPartnerLanguage() === 'en';
    const isCertCenter = variant === 'cert-centers';
    const partnerLabel = isCertCenter ? certCentersRu.hero.partnerLabel : 'Transport companies';
    const description = isCertCenter
        ? certCentersRu.hero.description
        : 'Vedmarket connects you with importers who have already calculated taxes and duties';

    let title: React.ReactNode = 'Spend less time on quotes. Get qualified shipping requests.';

    if (isEnglish) {
        title = <>Spend less time on quotes.<br/>Get qualified shipping requests.</>;
    }

    if (isCertCenter) {
        title = certCentersRu.hero.title;
    }

    const links = navigationLinks.map(link => ({
        ...link,
        href: partnerPath(link.href),
        children: link.children?.map(child => ({...child, href: partnerPath(child.href)})),
    }));

    const startRef = useRef<HTMLDivElement | null>(null);
    const mobileMenuRef = useRef<HTMLDivElement | null>(null);
    const desktopHeaderRef = useRef<HTMLDivElement | null>(null);
    const desktopMenuOwner = useRef<boolean | null>(null);
    const lastScrollY = useRef(0);
    const mobileMenuAnimationFrame = useRef<number | undefined>();

    const [showHeaderScroll, setShowHeaderScroll] = useState(false);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
    const [openDesktopMenu, setOpenDesktopMenu] = useState<string | null>(null);
    const [isTablet, setIsTablet] = useState(window.innerWidth >= TABLET_BREAKPOINT);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= DESKTOP_BREAKPOINT);
    const [transitionIsActive, setTransitionIsActive] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const handleResize = () => {
            setIsTablet(window.innerWidth >= TABLET_BREAKPOINT);
            setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);

            if (window.innerWidth < DESKTOP_BREAKPOINT) {
                setOpenDesktopMenu(null);
                desktopMenuOwner.current = null;
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (!startRef.current) return;

            const startBottom = startRef.current.getBoundingClientRect().bottom;
            const currentScrollY = window.scrollY;
            const startOutOfView = startBottom < 0;
            const scrollingUp = currentScrollY < lastScrollY.current;
            const shouldShowHeader = startOutOfView && scrollingUp;
            const initialHeaderBottom = desktopHeaderRef.current?.getBoundingClientRect().bottom ?? 0;
            const initialMenuHidden = desktopMenuOwner.current === false && initialHeaderBottom <= 0;
            const stickyMenuHidden = desktopMenuOwner.current === true && !shouldShowHeader;

            setShowHeaderScroll(shouldShowHeader);

            if (initialMenuHidden || stickyMenuHidden) {
                setOpenDesktopMenu(null);
                desktopMenuOwner.current = null;
            }

            lastScrollY.current = currentScrollY;
        };

        window.addEventListener("scroll", handleScroll);

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (menuIsOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [menuIsOpen]);

    const animateMobileMenu = (shouldShow: boolean, onComplete?: () => void) => {
        const menu = mobileMenuRef.current;

        if (!menu) {
            onComplete?.();
            return;
        }

        if (mobileMenuAnimationFrame.current) {
            window.cancelAnimationFrame(mobileMenuAnimationFrame.current);
        }

        const from = shouldShow ? 0 : Number(window.getComputedStyle(menu).opacity) || 1;
        const to = shouldShow ? 1 : 0;
        const startedAt = window.performance.now();

        if (shouldShow) {
            menu.style.display = 'block';
            menu.style.visibility = 'visible';
            menu.style.pointerEvents = 'auto';
            menu.style.opacity = `${from}`;
        }

        const fade = (now: number) => {
            const progress = Math.min((now - startedAt) / MOBILE_MENU_ANIMATION_DURATION, 1);
            const opacity = from + (to - from) * swingProgress(progress);

            menu.style.opacity = `${opacity}`;

            if (progress < 1) {
                mobileMenuAnimationFrame.current = window.requestAnimationFrame(fade);
                return;
            }

            menu.style.opacity = `${to}`;
            mobileMenuAnimationFrame.current = undefined;

            if (!shouldShow) {
                menu.style.display = 'none';
                menu.style.visibility = 'hidden';
                menu.style.pointerEvents = 'none';
            }

            onComplete?.();
        };

        mobileMenuAnimationFrame.current = window.requestAnimationFrame(fade);
    }

    const handleOpenMenu = () => {
        if (menuIsOpen) {
            return;
        }

        flushSync(() => {
            setMenuIsOpen(true);
        });
        animateMobileMenu(true);
    }

    const handleCloseMenu = () => {
        if (!menuIsOpen) {
            return;
        }

        animateMobileMenu(false, () => {
            setMenuIsOpen(false);
            setOpenMobileSubmenu(null);
        });
    }

    const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>, href: string, shouldCloseMenu: boolean) => {
        const target = document.getElementById(href.replace('#', ''));

        if (!target) {
            return;
        }

        event.preventDefault();

        if (shouldCloseMenu) {
            handleCloseMenu();
            setTransitionIsActive(false);
            document.body.style.overflow = '';

            setTimeout(function() {
                setTransitionIsActive(true);
            }, 1000);
        }

        target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }

    const handleClickMobileLink = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        handleAnchorClick(event, href, true);
    }

    const handleClickMobileDropdown = (event: React.MouseEvent<HTMLAnchorElement>, text: string) => {
        event.preventDefault();
        setOpenMobileSubmenu((current) => current === text ? null : text);
    }

    const handleClickLink = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        setOpenDesktopMenu(null);
        handleAnchorClick(event, href, false);
    }

    const resetMobileMenuTransition = () => {
        setTransitionIsActive(false);

        setTimeout(function() {
            setTransitionIsActive(true);
        }, 1000);
    }

    const isAnchorLink = (href: string) => href.startsWith('#');
    const isExternalLink = (href: string) => /^https?:\/\//.test(href);
    const renderMenuIcon = () => menuIsOpen ? <Close className={styles.mobileMenuIcon}/> : <MenuIcon className={styles.mobileMenuIcon}/>;

    const CompactHeaderLogo = isTablet ? LogoVedmarketMiddle : LogoVedmarket;
    const HeaderLogo = isDesktop ? LogoVedmarketBig : CompactHeaderLogo;

    const handleClickDesktopDropdown = (event: React.MouseEvent<HTMLButtonElement>, text: string, isScrolled: boolean) => {
        event.preventDefault();
        desktopMenuOwner.current = isScrolled;
        setOpenDesktopMenu((current) => current === text ? null : text);
    };

    const renderMobileLink = ({href, text, isNewTab}: HeroLink, isChild = false, onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void) => {
        const className = cx(styles.linkMobile, {
            [styles.linkMobileChild]: isChild,
        });
        const content = <Menu>{typografText(text)}</Menu>;

        if (isAnchorLink(href)) {
            return (
                <a href={href} className={className} onClick={onClick || ((event) => handleClickMobileLink(event, href))} rel="noreferrer" target={isNewTab ? '_blank' : '_self'}>
                    {content}
                </a>
            );
        }

        if (isExternalLink(href)) {
            return (
                <a href={href} className={className} onClick={onClick || resetMobileMenuTransition} rel="noreferrer" target={isNewTab ? '_blank' : '_self'}>
                    {content}
                </a>
            );
        }

        return (
            <Link to={href} className={className} onClick={resetMobileMenuTransition} target={isNewTab ? '_blank' : '_self'}>
                {content}
            </Link>
        );
    };

    const renderDesktopLink = ({href, text, isNewTab}: HeroLink, isSubmenu = false) => {
        const Text = isSubmenu ? SubmenuText : DesktopMenuText;

        if (isAnchorLink(href)) {
            return (
                <a href={href} className={styles.link} onClick={(event) => handleClickLink(event, href)} target={isNewTab ? '_blank' : '_self'} rel="noreferrer">
                    <Text>{typografText(text)}</Text>
                </a>
            );
        }

        if (isExternalLink(href)) {
            return (
                <a href={href} className={styles.link} onClick={() => setOpenDesktopMenu(null)} target={isNewTab ? '_blank' : '_self'} rel="noreferrer">
                    <Text>{typografText(text)}</Text>
                </a>
            );
        }

        return (
            <Link to={href} className={styles.link} onClick={() => setOpenDesktopMenu(null)} target={isNewTab ? '_blank' : '_self'}>
                <Text>{typografText(text)}</Text>
            </Link>
        );
    };

    const renderDesktopNavItem = (link: HeroLink, isScrolled: boolean) => {
        const {href, text, children} = link;
        const isDropdownOpen = openDesktopMenu === text && desktopMenuOwner.current === isScrolled;
        const isPartnersActive = text === 'Partners' && /^\/(en\/)?partners/.test(location.pathname);

        if (!children || (text !== 'Partners' && text !== 'More')) {
            return (
                <li key={`${href}-${text}`} className={styles.navLink}>
                    {renderDesktopLink(link)}
                </li>
            );
        }

        return (
            <li key={`${href}-${text}`} className={cx(styles.navLink, styles.navLinkWithDropdown)}>
                <button
                    type="button"
                    className={cx(styles.link, styles.desktopDropdownTrigger, {
                        [styles.desktopDropdownTriggerActive]: isPartnersActive,
                        [styles.desktopDropdownTriggerOpen]: isDropdownOpen,
                    })}
                    aria-expanded={isDropdownOpen}
                    onClick={(event) => handleClickDesktopDropdown(event, text, isScrolled)}>
                    <DesktopMenuText>{typografText(text)}</DesktopMenuText>
                    <ArrowDropDown className={styles.stickyDropdownIcon} aria-hidden="true"/>
                </button>
                {isDropdownOpen && (
                    <ul className={styles.desktopSubmenu}>
                        {children.map((child) => (
                            <li key={`${child.href}-${child.text}`} className={styles.desktopSubmenuItem}>
                                {renderDesktopLink(child, true)}
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        );
    };

    const renderDesktopHeader = (isScrolled = false) => (
        <div ref={isScrolled ? undefined : desktopHeaderRef} className={cx(styles.desktopHeader, {
            [styles.desktopHeaderScrolled]: isScrolled,
        })}>
            <a href="https://vedmarket.ru/" className={styles.logoContainer}>
                <LogoVedmarketBig aria-label={translatePartnerText('Vedmarket')} className={cx(styles.link, styles.desktopLogo)} width={352} height={18}/>
            </a>
            <div className={styles.menu}>
                <nav className={styles.navbar}>
                    <ul className={styles.navbarList}>
                        {links.map((link) => renderDesktopNavItem(link, isScrolled))}
                        <li className={cx(styles.navLink, styles.authLinks)}>
                            <a href={LOGIN_LINK} className={styles.link} target="_blank" rel="noreferrer">
                                <DesktopMenuText>Log in</DesktopMenuText>
                            </a>
                            <span className={styles.authDivider}>|</span>
                            <a href={REGISTER_LINK} className={styles.link} target="_blank" rel="noreferrer">
                                <DesktopMenuText>Sign up</DesktopMenuText>
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );

    const renderMobileHeader = () => (
        <div className={styles.headerContainer}>
            <a href="https://vedmarket.ru/" className={styles.logoContainer}>
                <HeaderLogo aria-label={translatePartnerText('Vedmarket')} className={cx(styles.logo, styles.link, styles.compactLogo)}/>
            </a>
            <div className={cx(styles.menuContainer, {
                [styles.menuContainerOpen]: menuIsOpen,
            })}>
                <button type="button" className={styles.menuButton} onClick={menuIsOpen ? handleCloseMenu : handleOpenMenu}>
                    {renderMenuIcon()}
                </button>
            </div>
        </div>
    );

    const background = (
        <div className={styles.backgroundImage} aria-hidden="true"/>
    );

    return (
        <>
            <div className={cx(styles.headerScroll, {
                [styles.headerScrollVisible]: showHeaderScroll,
                [styles.headerScrollHidden]: !showHeaderScroll,
                [styles.headerScrollMenuOpen]: menuIsOpen,
            })}>
                <div className={cx(styles.sectionContainer, styles.headerScrollDesktopContainer)}>
                    {renderDesktopHeader(true)}
                </div>
                {renderMobileHeader()}
            </div>
            <div className={styles.section} ref={startRef} id="partners-start">
                <div className={cx(styles.mobileNavigation, {
                    [styles.mobileNavigationOpen]: menuIsOpen,
                })}>
                    <div id="partners-start-menu" ref={mobileMenuRef} className={cx(styles.menuContainerMobile, {
                        [styles.menuContainerMobileTransition] : transitionIsActive,
                        [styles.menuContainerMobileActive] : menuIsOpen,
                        [styles.menuContainerMobileWithScrolledHeader] : showHeaderScroll,
                    })}>
                        <div className={styles.mobileMenuContent}>
                            <nav className={styles.navbarMobile}>
                                <ul className={styles.navbarListMobile}>
                                    {links.map((link) =>
                                        <li key={`${link.href}-${link.text}`} className={styles.navLinkMobile}>
                                            {link.children ? renderMobileLink(link, false, (event) => handleClickMobileDropdown(event, link.text)) : renderMobileLink(link)}
                                            {link.children && openMobileSubmenu === link.text && (
                                                <ul className={styles.navbarSubListMobile}>
                                                    {link.children.map((child) => (
                                                        <li key={`${child.href}-${child.text}`} className={styles.navSubLinkMobile}>
                                                            {renderMobileLink(child, true)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    )}
                                    <li className={cx(styles.navLinkMobile, styles.mobileAuthLinks)}>
                                        <a href={LOGIN_LINK} className={styles.linkMobile} target="_blank" rel="noreferrer">
                                            <Menu>Log in</Menu>
                                        </a>
                                        <span className={styles.mobileAuthDivider}>|</span>
                                        <a href={REGISTER_LINK} className={styles.mobileAuthLink} target="_blank" rel="noreferrer">
                                            <Menu>Sign up</Menu>
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                            <div className={styles.mobileLink}>
                                <ContainedButton>Become a partner</ContainedButton>
                                <OutlinedButton>Watch demo</OutlinedButton>
                            </div>
                        </div>
                    </div>
                    <div className={styles.mobileHeader}>
                        {renderMobileHeader()}
                    </div>
                </div>
                {backgroundContainer ? createPortal(background, backgroundContainer) : background}
                <div className={cx(styles.sectionContainer, {
                    [styles.certCenterContent]: isCertCenter,
                })}>
                    {renderDesktopHeader()}
                    <div className={styles.sectionBreadCrumbs}>
                        <IntroSubtitle sx={{
                            display: 'block',
                            '@media screen and (max-width: 575px)': {
                                '& > :last-child': {
                                    display: 'block',
                                },
                            },
                        }}>
                            <span>{typografText('Partners')}</span>{' '}
                            <span className={styles.accent}>→</span>{' '}
                            <span>{typografText(partnerLabel)}</span>
                        </IntroSubtitle>
                    </div>
                    <div className={styles.mainTitleContainer}>
                        <div className={styles.title}>
                            <IntroHeader sx={{
                                hyphens: isCertCenter ? 'auto' : 'none',
                                '--typography-word-hyphens': isCertCenter ? 'auto' : 'none',
                                wordBreak: 'normal',
                                overflowWrap: 'normal',
                            }}>{title}</IntroHeader>
                        </div>
                        <IntroBody sx={{
                            display: 'flex',
                            alignItems: isCertCenter ? 'flex-start' : 'center',
                            '@media screen and (min-width: 768px) and (max-width: 1199px)': {
                                columnGap: '12px',
                                fontSize: '18px',
                                lineHeight: 1.6,
                            },
                            '@media screen and (min-width: 576px) and (max-width: 767px)': {
                                alignItems: 'flex-start',
                                fontSize: '16px',
                                lineHeight: 1.6,
                            },
                        }}>
                        <span className={styles.emoji}>
                            {EMOJIS.HANDSHAKE_LIGHT}
                        </span>
                            {description}</IntroBody>
                    </div>
                    <div className={styles.buttonsBlock}>
                        <ContainedButton isWhite>Become a partner</ContainedButton>
                        <OutlinedButton isBlack>Watch demo</OutlinedButton>
                    </div>
                </div>
            </div>
        </>
    );
}

export default HeroSection
